/**
 * Shop Domain - Product Card Components
 * 
 * Aurora-compliant product cards using UI primitives
 * All styling uses design tokens from tokens.css
 */

import React, { Fragment, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Card from '../../../ui/Card';
import { Text } from '../../../ui/Text';
import Badge from '../../../ui/Badge';

/**
 * Product card with quantity controls for shopping cart
 * Used in product detail pages
 */
export const ProductCard = ({ data, addToCart, layout = 'vertical' }) => {
  const [quantity, setQuantity] = useState(data.quantity || 0);

  useEffect(() => {
    setQuantity(data.quantity || 0);
  }, [data.quantity]);

  const handleAdd = () => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    addToCart(data, newQuantity);
  };

  const handleRemove = () => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      addToCart(data, newQuantity);
    }
  };

  const cardClasses = layout === 'horizontal' 
    ? 'flex items-center gap-3' 
    : 'flex-col';

  return (
    <Card 
      variant="default" 
      padding="sm" 
      className="w-full"
    >
      <div className={cardClasses}>
        {/* Product Image */}
        <div className="relative w-[140px] h-[100px] flex-shrink-0 rounded-xl overflow-hidden">
          {data.discount && (
            <div className="absolute left-2 top-2 z-10">
              <Badge variant="success" size="sm">
                {data.discount}
              </Badge>
            </div>
          )}
          <img 
            src={data.img} 
            className="w-full h-full object-cover" 
            alt={data.title}
          />
        </div>

        {/* Product Info */}
        <div className="flex-1">
          <Text size="lg" weight="semibold" className="mb-1">
            {data.title}
          </Text>
          <Text size="sm" color="secondary" lineClamp={2} className="mb-2">
            {data.desc}
          </Text>

          {/* Price and Quantity Controls */}
          <div className="flex items-center justify-between">
            <Text size="base" weight="semibold" color="brand">
              ${data.price}
            </Text>

            <div className="flex items-center gap-2">
              {quantity > 0 && (
                <>
                  <button
                    onClick={handleRemove}
                    className="w-6 h-6 flex items-center justify-center rounded-full 
                             bg-background-secondary hover:bg-background-tertiary 
                             transition-colors cursor-pointer"
                    aria-label="Decrease quantity"
                  >
                    <img src="/images/Tab (1).svg" alt="" />
                  </button>
                  <div className="w-6 h-6 flex items-center justify-center rounded-full 
                                bg-background-secondary text-sm font-medium">
                    {quantity}
                  </div>
                </>
              )}
              <button
                onClick={handleAdd}
                className="w-6 h-6 flex items-center justify-center rounded-full 
                         bg-primary hover:bg-primary-hover 
                         transition-colors cursor-pointer"
                aria-label="Increase quantity"
              >
                <img src="/images/Tab.svg" alt="" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

/**
 * Restaurant/Food preview card with link
 * Used in search and browse pages
 */
export const RestaurantCard = ({ data }) => {
  return (
    <NavLink to="/detail/restaurant" className="block w-full">
      <Card variant="default" padding="none" hoverable>
        {/* Restaurant Image */}
        <div className="relative w-full h-[186px] rounded-t-lg overflow-hidden">
          {data.like && (
            <div className="absolute left-3 top-3 z-10">
              <Badge variant="default" size="sm">
                <img 
                  src="/images/like.png" 
                  alt="" 
                  className="w-4 h-4 inline mr-1"
                />
                {data.like}
              </Badge>
            </div>
          )}
          <img 
            src={data.img} 
            className="w-full h-full object-cover" 
            alt={data.title}
          />
        </div>

        {/* Restaurant Info */}
        <Card.Body>
          <Text size="base" weight="medium" className="mb-1">
            {data.title}
          </Text>
          <div className="flex items-center gap-2">
            <Text size="sm" color="secondary">
              {data.distance}
            </Text>
            <div className="w-1 h-1 rounded-full bg-text-tertiary" />
            <Text size="sm" color="secondary">
              {data.time}
            </Text>
          </div>
        </Card.Body>
      </Card>
    </NavLink>
  );
};

// Backwards compatibility exports
export const CardFoodToBuy = ProductCard;
export const CardFoodRestaurant = (props) => <ProductCard {...props} layout="horizontal" />;
export const CardFood = RestaurantCard;
